import _ from 'lodash';
import React, { ReactComponentElement } from 'react';

// models and services
import { FullUserModel, StatusCode } from 'src/types/user.model';
import FormatService from '../../../services/format.service';
import LiteralsService from '../../../services/literals/LiteralsService';
import NavigationService from '../../../services/navigation.service';
import UnitService from '../../../services/unit.service';

// components
import BasicDrawer from '../common/BasicDrawer';
import { GlyphPlus, GlyphRight } from '@americanexpress/dls-react';
import MoreInfoTooltip from '../../../components/more-info-tooltip/MoreInfoTooltip';
import { HELP_DOMAIN } from 'src/utils/constants';
import Typography from '../../../components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';

// icons and stylesheets
import { ReactComponent as BudgetIcon } from './icons/budget.svg';
import { ReactComponent as InsightsIcon } from './icons/insights.svg';
import { ReactComponent as LifeEventsIcon } from './icons/life-events.svg';
import { ReactComponent as VerticalTimelineIcon } from './icons/vertical-timeline.svg';
import { ReactComponent as StatusOfftrackImage } from './status-offtrack.svg';
import { ReactComponent as StatusOntrackImage } from './status-ontrack.svg';
import { ReactComponent as SyncAccountsIcon } from './icons/sync-accounts.svg';
import './status.scss';
import './StatusDrawerAlternate.scss';
import { TooltipPlacement } from 'src/types/tooltip-placement.enum';
import Selectors from 'src/utils/selectors';

export class StatusData {
    class: string;
    image: JSX.Element;
    text: string;
}

export default class StatusDrawerAlternate extends BasicDrawer<any, any> {
    public override render(): ReactComponentElement<any> {
        const user: FullUserModel = this.props.user;

        // prevent fatal errors on missing data
        if (!user || !user.cashFlow || !user.rawCashFlow) {
            return null;
        }

        // prepare display data
        const drawerProps: any = this.props.drawerProps;
        const statusData: StatusData = this.getStatusData(user.status);
        const formattedNetWorth: string = this.getNetWorth();
        const networthUrl: string = this.getNetWorthUrl();
        const statusHelpUrl: string = this.getStatusHelpUrl();
        // sometimes summary is missing
        const accountErrors = _.get(drawerProps, 'summary.accountErrors');
        const overBudget = _.get(drawerProps, 'summary.overBudget');
        const warnBudget = _.get(drawerProps, 'summary.warnBudget');
        const insights = _.get(drawerProps, 'summary.insights');
        const countBudgets = _.get(drawerProps, 'summary.countBudgets');

        let accountsStatus: JSX.Element = null;
        if (accountErrors) {
            accountsStatus = (
                <span className="status">
                    <span className="bad-dot" />
                    <span className="text">
                        There is a <span className="bad">problem</span> with {accountErrors}{' '}
                        {accountErrors > 1 ? 'accounts' : 'account'}
                    </span>
                </span>
            );
        }

        let budgetStatus: JSX.Element = null;
        if (overBudget) {
            budgetStatus = (
                <span className="status">
                    <span className="bad-dot" />
                    <span className="text">
                        You're <span className="bad">overspending</span> in {overBudget}{' '}
                        {overBudget > 1 ? 'budgets' : 'budget'}
                    </span>
                </span>
            );
        }
        if (!overBudget && warnBudget) {
            budgetStatus = (
                <span className="status">
                    <span className="warn-dot" />
                    <span className="text">
                        <span className="warn">Close to overspending</span> in {warnBudget}{' '}
                        {warnBudget > 1 ? 'budgets' : 'budget'}
                    </span>
                </span>
            );
        }

        let insightStatus: JSX.Element = null;
        if (insights) {
            insightStatus = (
                <span className="status">
                    <span className="good-dot" />
                    <span className="text">
                        You have {insights === 1 ? 'a ' : ''}
                        <span className="good">new</span> {insights > 1 ? 'insights' : 'insight'}
                    </span>
                </span>
            );
        }

        // return view
        return (
            <div className="status-drawer-alternate drawer">
                <Typography variant={TypographyVariants.HEADING_XS} className="textStrong">
                    {LiteralsService.getAppName()}
                </Typography>
                {statusData.image}
                <Typography variant={TypographyVariants.SUBTEXT} className="plan-intro textDefault">
                    Your plan is
                </Typography>
                <Typography variant={TypographyVariants.HEADING_XL} className={`plan ${statusData.class}`}>
                    {statusData.text}
                    <MoreInfoTooltip
                        title={LiteralsService.getLiteral('StatusDrawer.planTooltipTitle')}
                        body={LiteralsService.getLiteral('StatusDrawer.planTooltip')}
                        link={statusHelpUrl}
                        linkName={LiteralsService.getLiteral('StatusDrawer.planStatusLink')}
                        buttonAriaLabel={LiteralsService.getLiteral('StatusDrawer.planTooltipTitle')}
                        placement={TooltipPlacement.BOTTOM}
                    />
                </Typography>
                <div className="networth">
                    <div className="networth-heading">
                        <Typography variant={TypographyVariants.SUBTEXT} className="textDefault">
                            Current net worth
                        </Typography>
                        <MoreInfoTooltip
                            title={LiteralsService.getLiteral('StatusDrawer.netWorthTooltipTitle')}
                            body={LiteralsService.getLiteral('StatusDrawer.netWorthTooltip')}
                            linkName={LiteralsService.getLiteral('StatusDrawer.netWorthLink')}
                            link={networthUrl}
                            buttonAriaLabel={LiteralsService.getLiteral('StatusDrawer.netWorthTooltipTitle')}
                            placement={TooltipPlacement.BOTTOM}
                        />
                    </div>
                    <Typography
                        variant={TypographyVariants.HEADING_L}
                        className={
                            this.props.currentNetWorth
                                ? 'networth-mentor textStrong'
                                : 'networth-mentor zero textStrong'
                        }
                    >
                        {formattedNetWorth}
                    </Typography>
                </div>

                <hr className="statusDivider" />

                <div className="status-drawer-buttons-container">
                    {UnitService.isNarrow() ? (
                        <button
                            className="status-drawer-button goals-button"
                            onClick={() => {
                                void NavigationService.goTo(
                                    this.props.drawerProps.history,
                                    '/timeline',
                                    'Timeline' + ' drawer'
                                );
                            }}
                        >
                            <VerticalTimelineIcon className="leftIcon" />
                            <div className="label">
                                <Typography variant={TypographyVariants.BODY} className="status-text">
                                    Your timeline
                                </Typography>
                            </div>
                            <GlyphRight className="clickIcon" />
                        </button>
                    ) : null}

                    <button
                        className="status-drawer-button goals-button"
                        onClick={() => {
                            void NavigationService.goTo(this.props.drawerProps.history, '/goals', 'Goals drawer');
                        }}
                    >
                        <LifeEventsIcon className="leftIcon" />
                        <div className="label">
                            <Typography variant={TypographyVariants.BODY} className="status-text">
                                Life Events
                            </Typography>
                        </div>
                        <GlyphPlus className="clickIcon" />
                    </button>
                    <button
                        className="status-drawer-button act-button"
                        onClick={() => {
                            void NavigationService.goTo(
                                this.props.drawerProps.history,
                                '/drawer/profile',
                                'Profile' + ' drawer'
                            );
                        }}
                    >
                        <SyncAccountsIcon className="leftIcon" />
                        <div className="label">
                            <Typography
                                variant={TypographyVariants.BODY}
                                className={accountsStatus ? 'status-text with-status' : 'status-text'}
                            >
                                Financial Profile
                                {accountsStatus}
                            </Typography>
                        </div>
                        <GlyphRight className="clickIcon" />
                    </button>
                    <button
                        className="status-drawer-button insights-button"
                        data-cy={Selectors.PlanAhead.INSIGHTS_MAIN_BUTTON}
                        onClick={() => {
                            void NavigationService.goTo(this.props.drawerProps.history, '/insights', 'Insights drawer');
                        }}
                    >
                        <InsightsIcon className="leftIcon" />
                        <div className="label">
                            <Typography
                                variant={TypographyVariants.BODY}
                                className={insightStatus ? 'status-text with-status' : 'status-text'}
                            >
                                Insights
                                {insightStatus}
                            </Typography>
                        </div>
                        <GlyphRight className="clickIcon" />
                    </button>
                    <button
                        className="status-drawer-button budget-button"
                        onClick={() => {
                            void NavigationService.goTo(
                                this.props.drawerProps.history,
                                countBudgets ? '/budget' : '/budget/start',
                                'Budget drawer'
                            );
                        }}
                    >
                        <BudgetIcon className="leftIcon" />
                        <div className="label">
                            <Typography
                                variant={TypographyVariants.BODY}
                                className={budgetStatus ? 'status-text with-status' : 'status-text'}
                            >
                                Budget
                                {budgetStatus}
                            </Typography>
                        </div>
                        <GlyphRight className="clickIcon" />
                    </button>
                </div>
            </div>
        );
    }

    private getNetWorth(): string {
        return Math.abs(this.props.currentNetWorth) < 10000000
            ? FormatService.format(this.props.currentNetWorth)
            : FormatService.formatCompactMoney(this.props.currentNetWorth);
    }

    private getNetWorthUrl(): string {
        return HELP_DOMAIN ? HELP_DOMAIN + '/articles/net-worth' : null;
    }

    private getStatusData(userStatus: StatusCode): StatusData {
        const statusData: StatusData = new StatusData();
        switch (userStatus) {
            case StatusCode.OffTrack:
                statusData.text = LiteralsService.getLiteral('StatusCode.OffTrack');
                statusData.class = 'offtrack';
                statusData.image = <StatusOfftrackImage className="status-image" />;
                break;
            case StatusCode.OnTrack:
                statusData.text = LiteralsService.getLiteral('StatusCode.OnTrack');
                statusData.class = 'ontrack';
                statusData.image = <StatusOntrackImage className="status-image" />;
                break;
        }

        return statusData;
    }

    private getStatusHelpUrl(): string {
        return HELP_DOMAIN ? HELP_DOMAIN + '/articles/status' : null;
    }
}
